/* =====================================================================
   Bluewud × Empire v13 — product card behaviour (single, small controller).
   1) Colour swatch -> swaps the card image to the matching variant image.
   2) Reveals the action buttons + compare control (the base hides them with a
      transition that beats CSS, so we set them inline once — no hover state to
      get out of sync). One init pass + one observer for cards added later.
   ===================================================================== */
(function () {
  'use strict';

  function sized(src, w) {
    if (!src) return src;
    try { var u = new URL(src, location.origin); u.searchParams.set('width', w); return u.href; }
    catch (e) { return src; }
  }
  function swap(img, src) {
    if (!img || !src) return;
    var u = sized(src, 600);
    ['srcset', 'data-srcset', 'data-widths'].forEach(function (a) { img.removeAttribute(a); });
    if (img.hasAttribute('data-src')) img.setAttribute('data-src', u);
    img.src = u;
  }

  function bindSwatches(item) {
    var dataEl = item.querySelector('script[data-swatch-data]');
    if (!dataEl || item.__bwSwatch) return;
    var d; try { d = JSON.parse(dataEl.textContent); } catch (e) { return; }
    if (!d.swatchOptionKey || !Array.isArray(d.variants)) return;
    var box = dataEl.closest('[data-swatches]');
    var img = item.querySelector('.productitem--image-primary');
    var alt = item.querySelector('.productitem--image-alternate');
    if (!box || !img) return;
    item.__bwSwatch = true;

    var preload = function () {
      d.variants.forEach(function (v) {
        var s = v.featured_image && (v.featured_image.src || v.featured_image);
        if (s) { var im = new Image(); im.src = sized(s, 600); }
      });
    };
    ('requestIdleCallback' in window) ? requestIdleCallback(preload) : setTimeout(preload, 1200);

    box.querySelectorAll('input[name="swatch"]').forEach(function (inp) {
      inp.addEventListener('change', function () {
        var v = d.variants.find(function (x) { return String(x[d.swatchOptionKey]) === String(inp.value); });
        if (!v || !v.featured_image) return;
        var s = v.featured_image.src || v.featured_image;
        swap(img, s);
        if (alt) swap(alt, s);
      });
    });
  }

  // Build our OWN Quick Shop / Choose Options buttons. The native ones are
  // broken in this build (dead clicks, ripple leaking out of bounds, and
  // empire.js fights any JS that reveals them). Ours are hidden by default and
  // revealed on hover with pure CSS (.bw-actions) — no JS race. Both link to the
  // product page for now; the native quick-shop popup is non-functional and
  // would need a separate rebuild.
  function buildCustomActions(item) {
    if (item.__bwBuilt || item.querySelector('.bw-actions')) return;
    item.__bwBuilt = true;
    var link = item.querySelector('.productitem--image-link') || item.querySelector('.productitem--title a');
    var url = link && link.getAttribute('href');
    if (!url) return;
    var hm = url.match(/\/products\/([^/?#]+)/);
    var handle = hm ? hm[1] : '';
    var drawer = document.createElement('div');
    drawer.className = 'bw-actions';
    var quick = document.createElement('a');
    quick.className = 'bw-btn bw-btn--quick';
    quick.href = url;
    quick.setAttribute('data-handle', handle);
    quick.textContent = 'Quick Shop';
    var choose = document.createElement('a');
    choose.className = 'bw-btn bw-btn--choose';
    choose.href = url;
    choose.setAttribute('data-handle', handle);
    choose.textContent = 'Choose Options';
    drawer.appendChild(quick);
    drawer.appendChild(choose);
    item.appendChild(drawer);
  }

  // Hide the "recently viewed" section when it has no product cards (the CSS
  // :empty rule fails because the container holds whitespace). Guarded so it
  // never fights the MutationObserver.
  function hideEmptyRecentlyViewed() {
    document.querySelectorAll('.product-recently-viewed--section').forEach(function (sec) {
      var box = sec.querySelector('[data-recently-viewed-container]');
      var hasItems = box && box.querySelector('.productitem, [data-product-item]');
      var want = hasItems ? '' : 'none';
      if (sec.style.display !== want) sec.style.display = want;
    });
  }

  // ---- Desktop arrows for the horizontal product rows ----------------------
  // These rows are overflow-x:auto with the scrollbar deliberately hidden
  // (scrollbar-width:none + ::-webkit-scrollbar{height:0}). On touch you swipe,
  // but on desktop that left NO way to scroll: no scrollbar, no arrows, and the
  // wheel scrolls the page vertically — so only the first ~5 of 27 cards were
  // ever reachable. Add real arrows (plus shift+wheel / trackpad support).
  function buildRowNav(row) {
    if (!row || row.__bwRowNav) return;
    row.__bwRowNav = true;

    var wrap = row.parentElement;
    if (!wrap) return;
    if (getComputedStyle(wrap).position === 'static') wrap.style.position = 'relative';
    wrap.classList.add('bw-rownav-host');

    // The scrollLeft values that align each card to the row's start edge.
    // Derived from the cards themselves rather than card-width arithmetic:
    // the row carries scroll-padding, so a computed multiple of the card
    // pitch lands slightly off the snap point and the browser visibly yanks
    // it back after the animation. Measured live so it survives breakpoint
    // changes and late-loading images.
    function stops() {
      var pad = parseFloat(getComputedStyle(row).scrollPaddingLeft);
      if (isNaN(pad)) pad = 0;
      var max = Math.max(0, row.scrollWidth - row.clientWidth);
      var out = [];
      row.querySelectorAll('.productgrid--item').forEach(function (card) {
        var at = Math.max(0, Math.min(max, Math.round(card.offsetLeft - pad)));
        // Every trailing card clamps to max; keep that stop only once so the
        // arrow never "clicks" without moving.
        if (!out.length || at - out[out.length - 1] > 4) out.push(at);
      });
      return out;
    }

    function nearestStop(list, x) {
      var at = 0, best = Infinity;
      for (var i = 0; i < list.length; i++) {
        var d = Math.abs(list[i] - x);
        if (d < best - 1) { best = d; at = i; }
      }
      return at;
    }

    function mk(dir) {
      var b = document.createElement('button');
      b.type = 'button';
      b.className = 'bw-rownav bw-rownav--' + (dir < 0 ? 'prev' : 'next');
      b.setAttribute('aria-label', dir < 0 ? 'Scroll products left' : 'Scroll products right');
      b.innerHTML = '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="' +
        (dir < 0 ? 'm15 6-6 6 6 6' : 'm9 6 6 6-6 6') +
        '" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';
      b.addEventListener('click', function () {
        // One click advances exactly ONE card. Paging by a full viewport
        // skipped past cards the shopper never saw, and the leftover
        // remainder at the end parked the row mid-card.
        var list = stops();
        if (!list.length) return;
        var index = nearestStop(list, row.scrollLeft) + dir;
        var target = list[Math.max(0, Math.min(list.length - 1, index))];
        // NOTE: scrollBy/scrollTo with behavior:'smooth' is silently ignored on
        // these rows — the smooth animation fights scroll-snap-type:x (measured:
        // smooth => 0px moved, auto => moves). So animate scrollLeft ourselves;
        // snap then settles the row onto the nearest card on its own.
        var from = row.scrollLeft;
        var dist = target - from;
        if (!dist) return;
        var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        if (reduce) { row.scrollLeft = target; refresh(); return; }
        /* scroll-snap-type on the row re-snaps after EVERY programmatic
           scrollLeft write, so the browser yanked each animated frame onto
           the nearest snap point and the row appeared to jump instantly with
           no animation at all (owner-reported). Suspend snapping for the
           duration and restore it once we land -- the target is itself a
           snap point, so nothing shifts when snap comes back. */
        var snapWas = row.style.scrollSnapType;
        row.style.scrollSnapType = 'none';

        var t0 = null, DUR = 380, done = false;
        function finish() {
          if (done) return;
          done = true;
          row.scrollLeft = target;
          row.style.scrollSnapType = snapWas;
          refresh();
        }
        function frame(ts) {
          if (done) return;
          if (t0 === null) t0 = ts;
          var p = Math.min(1, (ts - t0) / DUR);
          var e = 1 - Math.pow(1 - p, 3); // easeOutCubic
          row.scrollLeft = from + dist * e;
          if (p < 1) requestAnimationFrame(frame); else finish();
        }
        requestAnimationFrame(frame);
        // Backstop: if rAF is throttled (background tab, instant-nav prefetch)
        // the row would otherwise never move at all.
        setTimeout(finish, DUR + 90);
      });
      wrap.appendChild(b);
      return b;
    }

    var prev = mk(-1), next = mk(1);

    function refresh() {
      var max = row.scrollWidth - row.clientWidth;
      var x = row.scrollLeft;
      var scrollable = max > 4;
      wrap.classList.toggle('bw-rownav-host--on', scrollable);
      prev.disabled = !scrollable || x <= 2;
      next.disabled = !scrollable || x >= max - 2;
    }

    row.addEventListener('scroll', refresh, { passive: true });
    window.addEventListener('resize', refresh);
    // Trackpad horizontal / shift+wheel → scroll the row instead of the page.
    row.addEventListener('wheel', function (e) {
      if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) return; // native horizontal already works
      if (!e.shiftKey) return;
      var max = row.scrollWidth - row.clientWidth;
      if (max <= 4) return;
      e.preventDefault();
      row.scrollLeft += e.deltaY;
    }, { passive: false });

    refresh();
    setTimeout(refresh, 400); // after images settle
  }

  function init(scope) {
    (scope || document).querySelectorAll('.productitem').forEach(function (item) {
      bindSwatches(item);
      buildCustomActions(item);
    });
    (scope || document).querySelectorAll('.featured-collection__content, .product-row').forEach(buildRowNav);
    hideEmptyRecentlyViewed();
  }

  (document.readyState === 'loading')
    ? document.addEventListener('DOMContentLoaded', function () { init(); })
    : init();
  document.addEventListener('shopify:section:load', function (e) { init(e.target); });

  if ('MutationObserver' in window) {
    var pending = false;
    new MutationObserver(function (muts) {
      for (var i = 0; i < muts.length; i++) {
        if (muts[i].addedNodes && muts[i].addedNodes.length) {
          if (!pending) { pending = true; requestAnimationFrame(function () { pending = false; init(); }); }
          break;
        }
      }
    }).observe(document.documentElement, { childList: true, subtree: true });
  }
})();

/* =====================================================================
   Header cart count — stays in sync on every AJAX cart change (add /
   change / update / clear), no page refresh. Wraps fetch + XHR so it works
   regardless of which cart JS the theme/cart page uses.
   ===================================================================== */
(function () {
  'use strict';
  function refresh() {
    fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        document.querySelectorAll('.site-header-cart--count').forEach(function (el) {
          // The count is displayed by a CSS ::after (content: attr(data-header-cart-count)).
          // Only update the attribute + visibility — never textContent, or it doubles.
          el.textContent = '';
          if (cart.item_count > 0) {
            el.setAttribute('data-header-cart-count', cart.item_count);
            el.classList.add('visible');
          } else {
            el.setAttribute('data-header-cart-count', '');
            el.classList.remove('visible');
          }
        });
      })
      .catch(function () {});
  }
  var isCartCall = function (url) {
    return typeof url === 'string' && /\/cart\/(add|change|update|clear)(\.js)?/.test(url);
  };
  var origFetch = window.fetch;
  if (origFetch) {
    window.fetch = function () {
      var url = (arguments[0] && arguments[0].url) || arguments[0] || '';
      return origFetch.apply(this, arguments).then(function (res) {
        if (isCartCall(url)) setTimeout(refresh, 60);
        return res;
      });
    };
  }
  if (window.XMLHttpRequest) {
    var open = XMLHttpRequest.prototype.open;
    XMLHttpRequest.prototype.open = function (method, url) {
      try {
        this.addEventListener('load', function () { if (isCartCall(url)) setTimeout(refresh, 60); });
      } catch (e) {}
      return open.apply(this, arguments);
    };
  }
  document.addEventListener('DOMContentLoaded', refresh);
})();
